import { deepClone } from '@/util/objects.js';
import { toVfId } from 'commons/util/parse.js';
import { NONE, PENDING_NEW, PENDING_UPDATE } from 'commons/constants/user_moderation.js';

export const parseV2 = (data) => {
    setBans(data);
    const clone = deepClone(data);

    // if the timestamp is not in milliseconds yet, convert it
    if (clone.date_created.toString().length < 13) {
        [ 'date_created', 'date_followed', 'date_following', 'date_muted' ].forEach((key) => {
            if (clone[key]) {
                clone[key] *= 1000;
            }
        });
    }
    clone.date = clone.date_created;
    return clone;
};

const emailVerificationStatus = ({ email_pending, is_new_user }) => {
    if (!email_pending) {
        return NONE;
    }
    return is_new_user ? PENDING_NEW : PENDING_UPDATE;
};

const displayName = (preferred, currentUsername) => preferred || currentUsername.name || '';

const usernameModerationStatus = (pendingUsername, currentUsername) =>
    pendingUsername.moderation_status || currentUsername.moderation_status || NONE;

// auth-service V3 returns only currently-active bans, each as { section_uuid, level }. Map every entry
// onto the shape the moderation banned-badge consumes: a numeric per-site site_id and a ban_level.
// permanent is set so the shared isBanActive gate passes — the service has already filtered to active,
// and the V3 payload carries no client-visible expiry to evaluate.
// site_id is only derived when section_uuid is present: a ban missing it (e.g. an auth-service that
// predates section_uuid during a staggered rollout) must not crash parseV3 and break the whole queue —
// it simply won't match a site, so no badge renders, matching the pre-fix behaviour.
const mapActiveBans = (activeBans) => {
    const list = Array.isArray(activeBans) ? activeBans : Object.values(activeBans || {});
    return list.map((ban) => ({
        ...ban,
        site_id: ban.section_uuid ? toVfId(ban.section_uuid) : undefined,
        ban_level: ban.level,
        permanent: true,
    }));
};

export const parseV3 = (data) => {
    const clone = deepClone(data);
    const uuid = clone.uuid || clone.sub;
    const username_info = clone.username_info || {};
    const username_pending_info = clone.username_pending_info || {};
    const picture = clone.picture || '';
    const bans = mapActiveBans(clone.active_bans);
    const date_created = typeof clone.created_at === 'number' ? clone.created_at * 1000 : 0;

    return {
        ...clone,
        uuid,
        id: uuid ? toVfId(uuid) : clone.id,
        name: displayName(clone.preferred_username, username_info),
        username_pending: username_pending_info.name || '',
        username_moderation_status: usernameModerationStatus(username_pending_info, username_info),
        pic_large: picture,
        pic_small: picture,
        email: clone.email || '',
        email_pending: clone.email_pending || '',
        email_verification_status: emailVerificationStatus(clone),
        site_uuid: clone.site_uuid || clone.section_uuid,
        bans,
        total_bans: bans.length,
        date_created,
        date: date_created,
    };
};

const setBans = (data) => {
    data.bans = Object.values(data.bans || {});
    const total_bans = data.bans.length;

    data.total_bans = total_bans;
};
